/**
 * A HALF-FINISHED FORM, KEPT WHILE SOMEBODY GOES AND READS SOMETHING
 * ------------------------------------------------------------------
 * Added 24 August 2026. Somebody filling in the signup form who tapped
 * "terms of service" or "privacy notice" came back to an empty form and had
 * to start again - which on the student form means a name, an address,
 * a date of birth, a school year, a parent's email and a list of skills.
 *
 * That is worse than an annoyance. The two links people lost their work to
 * are the two the site most wants them to open, so the site was quietly
 * teaching people not to read them.
 *
 * WHERE IT IS KEPT, AND FOR HOW LONG
 * `sessionStorage`, not `localStorage`, on purpose: it belongs to this one
 * tab and is gone the moment the tab closes. A shared laptop in a school
 * library does not end up holding somebody's half-typed signup for the next
 * person to find.
 *
 * On top of that, a draft older than MAX_AGE is ignored. Coming back to a
 * form you were filling in this morning and finding it still there is
 * helpful; coming back three days later is confusing, and by then the person
 * is starting again anyway.
 *
 * ABOUT THE PASSWORD, WHICH IS SAVED WITH EVERYTHING ELSE
 * It is here deliberately, and here is the reasoning so it can be reversed
 * by somebody who disagrees. Leaving it out would mean the form is restored
 * with one empty box, which is exactly the sort of half-fix that reads as a
 * bug. The password is already sitting in the page's own memory the whole
 * time the form is open, in the same tab, on the same device; keeping it for
 * the walk to the privacy notice and back does not hand it to anybody new.
 * Anything able to read this tab's sessionStorage could already read the
 * login token next door in localStorage.
 *
 * If you want it out: add 'password' to the list passed as `skip` from the
 * two signup pages. Nothing else needs changing.
 */

const MAX_AGE_MS = 2 * 60 * 60 * 1000; // two hours

/**
 * Reads a saved draft back. Returns null when there is nothing usable, so a
 * caller can write `loadDraft(key) || defaults` and never think about it.
 *
 * Never throws. Private browsing can refuse sessionStorage outright, and a
 * signup form that crashes because it could not restore a draft would be a
 * far worse bug than the one this fixes.
 */
export function loadDraft(key) {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;

    const { savedAt, values } = JSON.parse(raw);
    if (!savedAt || Date.now() - savedAt > MAX_AGE_MS) {
      sessionStorage.removeItem(key);
      return null;
    }
    return values;
  } catch {
    return null;
  }
}

/** Saves a draft, stamped so loadDraft can tell how old it is. */
export function saveDraft(key, values) {
  try {
    sessionStorage.setItem(key, JSON.stringify({ savedAt: Date.now(), values }));
  } catch {
    // Full, or refused. Losing a draft is not worth interrupting anybody for.
  }
}

/**
 * Throws the draft away. Called once the account exists - leaving somebody's
 * password in the tab after they have finished with it has no upside at all.
 */
export function clearDraft(key) {
  try {
    sessionStorage.removeItem(key);
  } catch {
    // Nothing to do, and nothing depends on it.
  }
}

export const STUDENT_SIGNUP_DRAFT = 'cc.draft.studentSignup';
export const POSTER_SIGNUP_DRAFT = 'cc.draft.posterSignup';
